import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Button, Chip, DiamondCheckbox, Select, TextField, ViewTab } from "@legends/ui";
import {
  createCaveEvent,
  createCaveRollItem,
  createCaveTicketType,
  createUser,
  reclaimCaveCast,
  deleteCaveEvent,
  deleteCaveRollItem,
  deleteCaveTicketType,
  fetchCaveAccess,
  fetchCaveEvents,
  fetchCaveRollItems,
  fetchCaveTicketTypes,
  renameCaveEvent,
  renameCaveTicketType,
  reorderCaveEvents,
  reorderCaveTicketTypes,
  setCaveAccess,
  setCaveRollOrder,
  setLobbyPublishAccess,
  setEventAccess,
  setEventRecord,
  setEventAsk,
  setEventDelivery,
  setEventRehearsal,
  setRole,
  suppressGlobalRollItem,
  unsuppressGlobalRollItem,
  uploadCaveRollItem,
  type CaveAccessRow,
  type CaveEvent,
  type Delivery,
  type RollItem,
  type RollName,
  type TicketKind,
  type TicketType,
} from "./api";
import AdminModal from "./admin/AdminModal";
import { dropRollId, moveRollId, rollSlotLabel } from "./rollOrder";
import SearchableSelect from "./admin/SearchableSelect";
import { useListControls } from "./listControls";
import {
  RecordChoice,
  Section,
  alertStyle,
  cardGridStyle,
  cardStyle,
  errMsg,
  formatDateTime,
  formatKind,
  formatPrice,
  monoLabelStyle,
  rowStyle,
} from "./sections";
import GoLivePanel from "./GoLivePanel";
import DirectorConsole from "./DirectorConsole";

// Cave-scoped configuration tabs (ADR 0008): the Security · Events · Tickets
// setup surfaces for a single cave. Shared so both the Cave console and the
// super-admin portal can host them.

/** A native control (datetime/number/select) dressed to match the cave well. */
const wellInputStyle: CSSProperties = {
  boxSizing: "border-box",
  background: "var(--well)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius)",
  color: "var(--text-hi)",
  fontFamily: "var(--font-sans)",
  fontSize: "var(--fs-body)",
  padding: "7px 10px",
  outline: "none",
};

const fieldColumnStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
};

// Event-card action row (LSC-446): a dedicated, consistently-spaced button row below the
// title/metadata, so Go live / Direct / Assign / Delete stay aligned across Admin/Legend/
// Director and across short vs wrapped titles — instead of riding the wrapping metadata row.
const eventActionsRowStyle: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 8,
  margin: "2px 0 4px",
};

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor: string;
  children: string;
}) {
  return (
    <label htmlFor={htmlFor} style={monoLabelStyle}>
      {children}
    </label>
  );
}

// ── Security ─────────────────────────────────────────────────────────────────

export function CaveSecurityTab({
  caveId,
  tenant,
  canProvision = false,
}: {
  caveId: string;
  /** The cave's tenant (the authz domain a role is granted in). Required for the
   *  in-cave provisioning flow; omitted in the operator console (no provisioning). */
  tenant?: string;
  /** Whether the viewer may create users + grant roles (super-admin) — enables the
   *  in-cave "＋ Add a user" provisioning flow (LSC-366). Off in the operator console. */
  canProvision?: boolean;
}) {
  const [rows, setRows] = useState<CaveAccessRow[]>([]);
  // Whether the current viewer may delegate lobby-publish (admin publisher) — LSC-260.
  const [canDelegatePublish, setCanDelegatePublish] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchCaveAccess(caveId)
      .then((r) => {
        setRows(r.access);
        setCanDelegatePublish(r.canDelegatePublish);
      })
      .catch((err: unknown) => setError(errMsg(err)));
  }, [caveId]);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  const toggle = (username: string, allow: boolean) => {
    setError(null);
    setCaveAccess(caveId, username, allow)
      .then(load)
      .catch((err: unknown) => setError(errMsg(err)));
  };

  const togglePublish = (username: string, allow: boolean) => {
    setError(null);
    setLobbyPublishAccess(caveId, username, allow)
      .then(load)
      .catch((err: unknown) => setError(errMsg(err)));
  };

  // In-cave user provisioning (LSC-366): create an operator with role + THIS cave's
  // access in one coherent step, so a Director/Legend can sign in and see the cave
  // immediately — no detour out to global Security to grant access separately.
  const [showAdd, setShowAdd] = useState(false);
  const [nuUsername, setNuUsername] = useState("");
  const [nuName, setNuName] = useState("");
  const [nuMode, setNuMode] = useState<"password" | "sso">("password");
  const [nuPassword, setNuPassword] = useState("");
  const [nuEmail, setNuEmail] = useState("");
  const [nuRole, setNuRole] = useState<"director" | "legend">("director");
  const [addBusy, setAddBusy] = useState(false);
  const resetAdd = () => { setNuUsername(""); setNuName(""); setNuMode("password"); setNuPassword(""); setNuEmail(""); setNuRole("director"); };

  const provision = async () => {
    if (!tenant) return;
    if (!nuUsername.trim()) { setError("Username is required."); return; }
    if (nuMode === "password" && nuPassword.length < 8) { setError("A password account needs a password of at least 8 characters."); return; }
    // An SSO account is matched to the operator's first Google/Apple sign-in by
    // VERIFIED EMAIL (userForIdentity). Without it the pre-created account is an
    // orphan — the operator's real sign-in makes a fresh account with no role/cave
    // and they "see nothing" (LSC-366). So the email is required for SSO.
    if (nuMode === "sso" && !nuEmail.trim()) { setError("An SSO account needs the email they sign in with (Google/Apple), so their first sign-in lands on this account."); return; }
    setError(null);
    setAddBusy(true);
    try {
      // 1) account, 2) role (the persona/view), 3) THIS cave's access grant (cave_config).
      await createUser({
        username: nuUsername.trim(),
        ...(nuName.trim() ? { displayName: nuName.trim() } : {}),
        ...(nuMode === "password" ? { password: nuPassword } : { email: nuEmail.trim() }),
      });
      await setRole(nuUsername.trim(), nuRole, tenant, true);
      await setCaveAccess(caveId, nuUsername.trim(), true);
      resetAdd();
      setShowAdd(false);
      load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setAddBusy(false);
    }
  };

  // Facet options are the roles actually present, so the filter never offers an empty bucket.
  // Labels are Title-cased (not the row's UPPERCASE chip) so a getByText never collides with
  // the list rows.
  const roleFilterOptions = useMemo(
    () =>
      [...new Set(rows.flatMap((r) => r.roles))]
        .sort()
        .map((role) => ({ value: role, label: role.charAt(0).toUpperCase() + role.slice(1).replace(/_/g, " ") })),
    [rows],
  );
  const { toolbar, result } = useListControls(rows, {
    searchText: (r) => [r.username, r.displayName, ...r.roles],
    searchPlaceholder: "Search users…",
    sorts: [
      { key: "name", label: "Name (A–Z)", cmp: (a, b) => a.username.localeCompare(b.username) },
      { key: "role", label: "Role", cmp: (a, b) => (a.roles[0] ?? "").localeCompare(b.roles[0] ?? "") },
    ],
    facets: [
      {
        key: "role",
        label: "Role",
        options: roleFilterOptions,
        test: (r, v) => r.roles.includes(v),
      },
      {
        key: "access",
        label: "Access",
        options: [
          { value: "direct", label: "Direct access" },
          { value: "via", label: "Granted via role" },
          { value: "none", label: "No access" },
        ],
        test: (r, v) => (v === "direct" ? r.direct : v === "via" ? r.viaRole : !r.direct && !r.viaRole),
      },
    ],
  });

  return (
    <Section title="Security">
      {canProvision && tenant && (
        <div style={{ margin: "10px 0 0" }}>
          <Button size="sm" variant="accent" aria-label="Add a user to this cave" onClick={() => { resetAdd(); setError(null); setShowAdd(true); }}>
            ＋ Add a user
          </Button>
          <span style={{ marginLeft: 10, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            Creates an operator with a role and this cave&rsquo;s access in one step.
          </span>
        </div>
      )}
      {toolbar}
      <div style={{ marginTop: 12 }}>
        {result.map((r) => (
          <div key={r.username} style={rowStyle}>
            <span
              style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}
            >
              {r.username}
            </span>
            {r.displayName && (
              <span
                style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}
              >
                {r.displayName}
              </span>
            )}
            <span style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
              {r.roles.map((role) => (
                <Chip key={role}>{role.toUpperCase()}</Chip>
              ))}
            </span>
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              {r.viaRole ? (
                <span style={monoLabelStyle}>Via role</span>
              ) : r.direct ? (
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Revoke access for ${r.username}`}
                  onClick={() => toggle(r.username, false)}
                >
                  Revoke
                </Button>
              ) : (
                <Button
                  size="sm"
                  variant="accent"
                  aria-label={`Allow access for ${r.username}`}
                  onClick={() => toggle(r.username, true)}
                >
                  Allow
                </Button>
              )}
              {/* Lobby-publish delegation (LSC-260): a per-cave publish grant so a
                  trusted operator can publish without tenant-wide admin. Editable
                  only by an admin publisher; role publishers are read-only. */}
              {r.publishViaRole ? (
                <span style={monoLabelStyle}>Publishes · via role</span>
              ) : canDelegatePublish ? (
                <Button
                  size="sm"
                  variant={r.canPublish ? "muted" : "accent"}
                  aria-label={r.canPublish ? `Revoke publish for ${r.username}` : `Grant publish for ${r.username}`}
                  onClick={() => togglePublish(r.username, !r.canPublish)}
                >
                  {r.canPublish ? "Revoke publish" : "Grant publish"}
                </Button>
              ) : r.canPublish ? (
                <span style={monoLabelStyle}>Can publish</span>
              ) : null}
            </div>
          </div>
        ))}
        {result.length === 0 && !error && (
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-xs)",
              color: "var(--text-dim)",
            }}
          >
            {rows.length === 0 ? "No users." : "No users match your search."}
          </p>
        )}
      </div>
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
      {showAdd && tenant && (
        <AdminModal
          title="Add a user to this cave"
          confirmLabel={addBusy ? "Creating…" : "Create user"}
          busy={addBusy}
          onConfirm={() => void provision()}
          onCancel={() => { if (!addBusy) { resetAdd(); setError(null); setShowAdd(false); } }}
        >
          <div style={{ display: "grid", gap: 12, minWidth: 300 }}>
            <TextField label="Username" name="cave-new-username" value={nuUsername} onChange={setNuUsername} placeholder="e.g. jordan" />
            <TextField label="Name (optional)" name="cave-new-name" value={nuName} onChange={setNuName} placeholder="Jordan Rivera" />
            <Select
              label="Role"
              aria-label="Role"
              value={nuRole}
              onChange={(v) => setNuRole(v as "director" | "legend")}
              options={[
                { value: "director", label: "Director" },
                { value: "legend", label: "Legend" },
              ]}
            />
            <Select
              label="How they sign in"
              aria-label="How they sign in"
              value={nuMode}
              onChange={(v) => setNuMode(v as "password" | "sso")}
              options={[
                { value: "password", label: "Password (this admin sets it)" },
                { value: "sso", label: "Google / Apple (no password)" },
              ]}
            />
            {nuMode === "password" ? (
              <TextField label="Password" name="cave-new-password" type="password" value={nuPassword} onChange={setNuPassword} placeholder="at least 8 characters" />
            ) : (
              <>
                <TextField label="Google / Apple email" name="cave-new-email" type="email" value={nuEmail} onChange={setNuEmail} placeholder="jordan@example.com" />
                <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                  No password — they sign in with Google or Apple. Their first sign-in with <strong>this exact email</strong> links to this account (so they land on their role + cave). Without it, SSO makes a fresh empty account.
                </span>
              </>
            )}
            <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
              They&rsquo;ll get the <strong>{nuRole}</strong> role and access to this cave, so they can sign in and see it right away.
              {nuRole === "legend" && " Link them to the cave's Legend identity from the Overview tab if needed."}
            </span>
          </div>
        </AdminModal>
      )}
    </Section>
  );
}

// ── Events ───────────────────────────────────────────────────────────────────

export interface CaveEventsTabProps {
  caveId: string;
  showGoLive?: boolean;
  showDirect?: boolean;
  /** Whether the viewer may create/delete events — scheduling authority
   *  (catalog:write, admin-only; ADR 0168 / LSC-370). A cave-scoped director/legend
   *  reads + produces events but does not schedule them, so the create form and
   *  per-event Delete are hidden without it. Defaults off (read-only). */
  canWrite?: boolean;
  /** Optional account directory ({value: username, label}) for the per-event "Assign"
   *  control (LSC-403). When provided, the assignee is chosen from a searchable picker
   *  instead of a free-typed username (fewer typos / no invalid-user grants); omitted
   *  (e.g. the operator console, which can't read the directory) → the plain text field. */
  userOptions?: { value: string; label: string }[];
}

export function CaveEventsTab({
  caveId,
  showGoLive = true,
  showDirect = true,
  canWrite = false,
  userOptions,
}: CaveEventsTabProps) {
  const [events, setEvents] = useState<CaveEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [liveEventId, setLiveEventId] = useState<string | null>(null);
  const [directEventId, setDirectEventId] = useState<string | null>(null);
  // Auto-archived events (ADR 0094) are hidden from the default list; a toggle
  // reveals them (the archive is client-side filtering — no separate query).
  const [showArchived, setShowArchived] = useState(false);
  const [assignEventId, setAssignEventId] = useState<string | null>(null);
  const [assignName, setAssignName] = useState("");
  const [assignBusy, setAssignBusy] = useState(false);
  // False-start recovery (LSC-641): which event's "Reset to not-started" is awaiting
  // a two-step inline confirm, and whether a reclaim is in flight.
  const [reclaimConfirmId, setReclaimConfirmId] = useState<string | null>(null);
  const [reclaimBusy, setReclaimBusy] = useState(false);
  // Inline feedback shown AT the assign form (ADR 0078) — a failed Grant Access
  // must explain itself where the operator acted, not silently (the shared
  // error/notice render at the bottom of a potentially long event list).
  const [assignError, setAssignError] = useState<string | null>(null);
  // Rename-in-place + reorder (LSC-572 / ADR 0251) — admin (catalog-write) only.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const [title, setTitle] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [duration, setDuration] = useState("60");
  const [repeat, setRepeat] = useState(false);
  const [intervalDays, setIntervalDays] = useState("7");
  const [count, setCount] = useState("8");

  const load = useCallback(() => {
    fetchCaveEvents(caveId)
      .then(setEvents)
      .catch((err: unknown) => setError(errMsg(err)));
  }, [caveId]);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  const submit = () => {
    if (!title || !startsAt) return;
    setError(null);
    setNotice(null);
    createCaveEvent(caveId, {
      title,
      startsAt: new Date(startsAt).toISOString(),
      durationMinutes: Number(duration),
      ...(repeat
        ? {
            recurrence: {
              intervalDays: Number(intervalDays),
              count: Number(count),
            },
          }
        : {}),
    })
      .then((res) => {
        // A non-blocking overlap advisory (CF-5/LSC-372) rides alongside success.
        setNotice(
          `Created ${res.events.length} event${res.events.length === 1 ? "" : "s"}.` +
            (res.warning ? ` ${res.warning}` : ""),
        );
        setTitle("");
        setStartsAt("");
        load();
      })
      .catch((err: unknown) => setError(errMsg(err)));
  };

  const remove = (eventId: string) => {
    setError(null);
    deleteCaveEvent(caveId, eventId)
      .then(load)
      .catch((err: unknown) => setError(errMsg(err)));
  };

  // Rename an event in place (LSC-572 / ADR 0251): id + relationships preserved.
  const saveRename = (eventId: string) => {
    const t = renameValue.trim();
    if (!t) return;
    setError(null);
    renameCaveEvent(caveId, eventId, t)
      .then(() => {
        setRenameId(null);
        load();
      })
      .catch((err: unknown) => setError(errMsg(err)));
  };

  // Active events in server (display) order — the set the public read presents, and the
  // set the ↑/↓ reorder operates on (LSC-572). Archived events aren't reorderable.
  const orderable = useMemo(
    () =>
      events
        .filter((e) => !e.archivedAt)
        .slice()
        .sort(
          (a, b) =>
            (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.startsAt.localeCompare(b.startsAt),
        ),
    [events],
  );
  const eventOrderIndex = (eventId: string) => orderable.findIndex((e) => e.id === eventId);
  const moveEvent = (eventId: string, dir: -1 | 1) => {
    const ids = orderable.map((e) => e.id);
    const i = ids.indexOf(eventId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setError(null);
    reorderCaveEvents(caveId, ids)
      .then(load)
      .catch((err: unknown) => setError(errMsg(err)));
  };

  // Event-scoped production access (ADR 0038): grant a director/legend the right
  // to produce THIS event only, without cave-wide config.
  const assign = (eventId: string) => {
    const username = assignName.trim();
    if (!username) return;
    setError(null);
    setNotice(null);
    setAssignError(null);
    setAssignBusy(true);
    setEventAccess(caveId, eventId, username, true)
      .then(() => {
        setNotice(`Assigned ${username} to this event.`);
        setAssignName("");
        setAssignEventId(null);
      })
      // Surface the failure INLINE at the form (a Director with no cave membership,
      // an unknown username, a permission failure) rather than silently (ADR 0078).
      .catch((err: unknown) => setAssignError(errMsg(err)))
      .finally(() => setAssignBusy(false));
  };

  // False-start recovery (LSC-641): reset a CaveCast whose broadcast is stuck `live`
  // but never aired (an accidental console-open / Go-Live→Exit) back to not-started.
  const reclaim = (eventId: string) => {
    setError(null);
    setNotice(null);
    setReclaimBusy(true);
    reclaimCaveCast(caveId, eventId)
      .then(() => {
        setNotice("Reset to not-started — this CaveCast is live again for ticket holders.");
        setReclaimConfirmId(null);
        load();
      })
      .catch((err: unknown) => setError(errMsg(err)))
      .finally(() => setReclaimBusy(false));
  };

  const archivedCount = events.filter((e) => e.archivedAt).length;
  const visibleEvents = showArchived ? events : events.filter((e) => !e.archivedAt);
  // Search + sort compose ON TOP of the Show-archived toggle (which controls the base set).
  const { toolbar, result, sortKey } = useListControls(visibleEvents, {
    searchText: (e) => [e.title],
    searchPlaceholder: "Search CaveCasts…",
    sorts: [
      // CHRONOLOGICAL is the default (LSC-572 follow-up, 2026-11-01): the public schedule
      // + "Next in the Cave" follow event DATES, not a custom/merchandising order, so the
      // admin default matches what fans see. "Custom order" remains a DISTINCT view that
      // drives the ↑/↓ arrangement — but no longer the public schedule (AC 6).
      { key: "soon", label: "Soonest first", cmp: (a, b) => a.startsAt.localeCompare(b.startsAt) },
      { key: "late", label: "Latest first", cmp: (a, b) => b.startsAt.localeCompare(a.startsAt) },
      {
        key: "order",
        label: "Custom order (admin)",
        cmp: (a, b) =>
          (a.displayOrder ?? 0) - (b.displayOrder ?? 0) || a.startsAt.localeCompare(b.startsAt),
      },
    ],
  });
  // The ↑/↓ reorder arranges the DISTINCT Custom-order view only (it no longer changes
  // the public schedule, LSC-572). Showing the arrows while the list is date-sorted would
  // be a no-op the operator can't see, so reveal them only under the Custom-order view.
  const reorderable = sortKey === "order";
  return (
    <Section title="Events">
      {archivedCount > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
          <DiamondCheckbox
            checked={showArchived}
            aria-label="Show archived events"
            onChange={() => setShowArchived((v) => !v)}
          />
          <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
            Show archived ({archivedCount})
          </span>
        </div>
      )}
      {toolbar}
      <div style={{ ...cardGridStyle, marginTop: 12 }}>
        {result.map((e) => (
          <div key={e.id} style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span
                style={{
                  // Full-width so the title (variable length) sits on its own line and
                  // the metadata row below stays aligned across cards (LSC-386).
                  flexBasis: "100%",
                  fontSize: "var(--fs-body-2)",
                  color: "var(--text-hi)",
                }}
              >
                {e.title}
              </span>
              {e.archivedAt && <Chip aria-label={`${e.title} archived`}>ARCHIVED</Chip>}
              {e.liveNotAired && (
                <Chip color="var(--live, #e5484d)" aria-label={`${e.title} is live but not on air`}>
                  LIVE · NOT ON AIR
                </Chip>
              )}
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-2xs)",
                  letterSpacing: "var(--ls-label)",
                  color: "var(--text-dim)",
                }}
              >
                {formatDateTime(e.startsAt)}
              </span>
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-2xs)",
                  letterSpacing: "var(--ls-label)",
                  color: "var(--text-dim)",
                }}
              >
                {e.durationMinutes} MIN
              </span>
              {e.seriesId && <Chip color="var(--accent)">SERIES</Chip>}
            </div>
            {/* Action controls on their OWN dedicated row (LSC-446): a stable,
                consistently-spaced button row across Admin/Legend/Director, instead of
                sharing the wrapping title/metadata row (which drifted with title length
                and the differing per-surface button set). */}
            <div style={eventActionsRowStyle}>
                {showGoLive && (
                  <Button
                    size="sm"
                    variant={liveEventId === e.id ? "accent" : "default"}
                    aria-label={`Go live for ${e.title}`}
                    onClick={() =>
                      setLiveEventId((id) => (id === e.id ? null : e.id))
                    }
                  >
                    Go live
                  </Button>
                )}
                {showDirect && (
                  <Button
                    size="sm"
                    variant={directEventId === e.id ? "accent" : "muted"}
                    aria-label={`Direct ${e.title}`}
                    onClick={() =>
                      setDirectEventId((id) => (id === e.id ? null : e.id))
                    }
                  >
                    Direct
                  </Button>
                )}
                <Button
                  size="sm"
                  variant={assignEventId === e.id ? "accent" : "muted"}
                  aria-label={`Assign an operator to ${e.title}`}
                  onClick={() => {
                    setAssignName("");
                    setAssignError(null);
                    setAssignEventId((id) => (id === e.id ? null : e.id));
                  }}
                >
                  Assign
                </Button>
                {canWrite && (
                  <>
                    <Button
                      size="sm"
                      variant={renameId === e.id ? "accent" : "muted"}
                      aria-label={`Rename event ${e.title}`}
                      onClick={() => {
                        setRenameValue(e.title);
                        setRenameId((id) => (id === e.id ? null : e.id));
                      }}
                    >
                      Rename
                    </Button>
                    {reorderable && (
                    <Button
                      size="sm"
                      variant="muted"
                      aria-label={`Move ${e.title} up`}
                      disabled={eventOrderIndex(e.id) <= 0}
                      onClick={() => moveEvent(e.id, -1)}
                    >
                      ↑
                    </Button>
                    )}
                    {reorderable && (
                    <Button
                      size="sm"
                      variant="muted"
                      aria-label={`Move ${e.title} down`}
                      disabled={eventOrderIndex(e.id) < 0 || eventOrderIndex(e.id) >= orderable.length - 1}
                      onClick={() => moveEvent(e.id, 1)}
                    >
                      ↓
                    </Button>
                    )}
                    <Button
                      size="sm"
                      variant="muted"
                      aria-label={`Delete event ${e.title}`}
                      onClick={() => remove(e.id)}
                    >
                      Delete
                    </Button>
                  </>
                )}
                {/* False-start recovery (LSC-641): shown only when this CaveCast is
                    stuck `live` but never aired. A two-step inline confirm resets it
                    to not-started (keeps recordings) — the immediate counterpart to the
                    never-aired reaper (ADR 0239). */}
                {e.liveNotAired &&
                  (reclaimConfirmId === e.id ? (
                    <>
                      <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                        Reset? (keeps recordings)
                      </span>
                      <Button
                        size="sm"
                        variant="accent"
                        disabled={reclaimBusy}
                        aria-label={`Confirm reset ${e.title} to not-started`}
                        onClick={() => reclaim(e.id)}
                      >
                        {reclaimBusy ? "Resetting…" : "Reset"}
                      </Button>
                      <Button
                        size="sm"
                        variant="muted"
                        disabled={reclaimBusy}
                        aria-label="Cancel reset"
                        onClick={() => setReclaimConfirmId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="accent"
                      aria-label={`Reset ${e.title} to not-started`}
                      onClick={() => setReclaimConfirmId(e.id)}
                    >
                      Reset to not-started
                    </Button>
                  ))}
            </div>
            {canWrite && renameId === e.id && (
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "4px 0 2px" }}>
                <TextField
                  label="New title"
                  name={`rename-event-${e.id}`}
                  value={renameValue}
                  onChange={setRenameValue}
                  placeholder="Event title"
                />
                <Button size="sm" variant="accent" aria-label={`Save title for ${e.title}`} onClick={() => saveRename(e.id)}>
                  Save
                </Button>
                <Button size="sm" variant="muted" aria-label="Cancel rename" onClick={() => setRenameId(null)}>
                  Cancel
                </Button>
              </div>
            )}
            {/* Event-level recording opt-out (ADR 0080) — lowest override, wins
                over the cave + platform defaults. */}
            <div style={{ padding: "2px 0 6px" }}>
              <RecordChoice
                label="Record"
                value={e.record ?? null}
                inheritHint="inherits the cave default"
                onChange={(v) => {
                  setError(null);
                  setEventRecord(caveId, e.id, v)
                    .then(load)
                    .catch((err: unknown) => setError(errMsg(err)));
                }}
              />
            </div>
            {/* Event-level Ask-the-Legend default (ADR 0093) — seeds the live
                program's askDisabled at go-live; the operator can still toggle it live. */}
            <div style={{ padding: "2px 0 6px", display: "flex", alignItems: "center", gap: 8 }}>
              <DiamondCheckbox
                checked={!e.askDisabled}
                aria-label={`Ask the Legend for ${e.title}`}
                onChange={() => {
                  setError(null);
                  setEventAsk(caveId, e.id, !e.askDisabled ? true : false)
                    .then(load)
                    .catch((err: unknown) => setError(errMsg(err)));
                }}
              />
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text)" }}>
                Ask the Legend {e.askDisabled ? "off" : "on"}
              </span>
            </div>
            {/* Event-level private-rehearsal flag (ADR 0259) — a rehearsal event is
                hidden from every public read (events, tickets, discovery) + admission,
                yet still producible for crew. Use it for a dry-run slot. */}
            <div style={{ padding: "2px 0 6px", display: "flex", alignItems: "center", gap: 8 }}>
              <DiamondCheckbox
                checked={!!e.rehearsal}
                aria-label={`Rehearsal (private) for ${e.title}`}
                onChange={() => {
                  setError(null);
                  setEventRehearsal(caveId, e.id, !e.rehearsal)
                    .then(load)
                    .catch((err: unknown) => setError(errMsg(err)));
                }}
              />
              <span style={{ fontSize: "var(--fs-xs)", color: "var(--text)" }}>
                Rehearsal (private){e.rehearsal ? " — hidden from fans" : ""}
              </span>
            </div>
            {/* Event-level live-delivery (ADR 0140): WebRTC (low-latency, keeps
                Ask-the-Legend interactive) vs HLS (segmented; AirPlay/PiP/Cast +
                cheaper fan-out, at the cost of latency). Off by default; the server
                refuses a change while the broadcast is live. */}
            <div style={{ padding: "2px 0 6px", display: "flex", alignItems: "center", gap: 8 }}>
              <label
                htmlFor={`delivery-${e.id}`}
                style={{ fontSize: "var(--fs-xs)", color: "var(--text)" }}
              >
                Delivery
              </label>
              <select
                id={`delivery-${e.id}`}
                aria-label={`Live delivery for ${e.title}`}
                value={e.delivery ?? ""}
                onChange={(ev) => {
                  setError(null);
                  setEventDelivery(caveId, e.id, ev.target.value as Delivery)
                    .then(load)
                    .catch((err: unknown) => setError(errMsg(err)));
                }}
                style={{
                  fontSize: "var(--fs-xs)",
                  background: "var(--surface-quiet)",
                  color: "var(--text)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 6,
                  padding: "2px 6px",
                }}
              >
                <option value="">Auto (default)</option>
                <option value="webrtc">WebRTC — low latency</option>
                <option value="hls">HLS — AirPlay/PiP/Cast</option>
              </select>
            </div>
            {assignEventId === e.id && (
              <form
                aria-label={`Assign operator to ${e.title}`}
                onSubmit={(ev) => {
                  ev.preventDefault();
                  assign(e.id);
                }}
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: 10,
                  padding: "10px 0 4px",
                }}
              >
                {userOptions ? (
                  // Pick the operator from the account directory (LSC-403) — a searchable
                  // combobox, so a raw/mistyped username can't be granted event access.
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <label style={{ ...monoLabelStyle, display: "block", marginBottom: 4 }}>Director/legend</label>
                    <SearchableSelect
                      ariaLabel="Director/legend username"
                      options={userOptions}
                      value={assignName}
                      onChange={setAssignName}
                      placeholder="Search accounts…"
                    />
                  </div>
                ) : (
                  <TextField
                    label="Director/legend username"
                    name={`assign-${e.id}`}
                    value={assignName}
                    onChange={setAssignName}
                  />
                )}
                <Button
                  variant="accent"
                  type="submit"
                  disabled={assignBusy || !assignName.trim()}
                >
                  Grant event access
                </Button>
              </form>
            )}
            {assignEventId === e.id && assignError && (
              <p role="alert" style={{ ...alertStyle, margin: "0 0 6px" }}>
                Couldn't grant access: {assignError}
              </p>
            )}
            {showGoLive && liveEventId === e.id && (
              <GoLivePanel
                caveId={caveId}
                event={e}
                // Refetch on exit so an ended CaveCast (auto-archived on End, ADR 0094)
                // updates the list without a manual reload (LSC-384).
                onClose={() => {
                  setLiveEventId(null);
                  load();
                }}
              />
            )}
            {showDirect && directEventId === e.id && (
              <DirectorConsole
                caveId={caveId}
                event={e}
                onClose={() => {
                  setDirectEventId(null);
                  load();
                }}
              />
            )}
          </div>
        ))}
        {result.length === 0 && !error && (
          <p
            style={{
              margin: 0,
              gridColumn: "1 / -1",
              fontSize: "var(--fs-xs)",
              color: "var(--text-dim)",
            }}
          >
            {events.length === 0 ? "No events." : "No CaveCasts match your search."}
          </p>
        )}
      </div>

      {!canWrite && (
        <p style={{ ...monoLabelStyle, margin: "12px 0 0", color: "var(--text-dim)" }}>
          Scheduling CaveCasts is an admin task — you can produce and configure events, but only an admin creates or deletes them.
        </p>
      )}
      {canWrite && (
      <form
        aria-label="Add event"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <TextField
          label="Title"
          name="event-title"
          value={title}
          onChange={setTitle}
        />
        <div style={fieldColumnStyle}>
          <FieldLabel htmlFor="event-starts-at">Starts at</FieldLabel>
          <div style={{ position: "relative" }}>
            <input
              id="event-starts-at"
              className={!startsAt ? "event-starts-at--empty" : undefined}
              type="datetime-local"
              aria-label="Starts at"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              style={wellInputStyle}
            />
            {!startsAt && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute",
                  inset: "1px 32px 1px 1px",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: 9,
                  pointerEvents: "none",
                  background: "var(--well)",
                  color: "var(--text-dim)",
                  fontFamily: "var(--font-sans)",
                  fontSize: "var(--fs-body)",
                }}
              >
                Select date &amp; time
              </span>
            )}
          </div>
        </div>
        <div style={fieldColumnStyle}>
          <FieldLabel htmlFor="event-duration">Duration (min)</FieldLabel>
          <input
            id="event-duration"
            type="number"
            min={1}
            aria-label="Duration minutes"
            value={duration}
            onChange={(e) => setDuration(e.target.value)}
            style={{ ...wellInputStyle, width: 90 }}
          />
        </div>
        <Button
          size="sm"
          variant={repeat ? "accent" : "muted"}
          aria-label="Toggle repeat"
          onClick={() => setRepeat((v) => !v)}
        >
          Repeat
        </Button>
        {repeat && (
          <>
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="event-interval">Interval days</FieldLabel>
              <input
                id="event-interval"
                type="number"
                min={1}
                aria-label="Interval days"
                value={intervalDays}
                onChange={(e) => setIntervalDays(e.target.value)}
                style={{ ...wellInputStyle, width: 90 }}
              />
            </div>
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="event-count">Count</FieldLabel>
              <input
                id="event-count"
                type="number"
                min={1}
                aria-label="Count"
                value={count}
                onChange={(e) => setCount(e.target.value)}
                style={{ ...wellInputStyle, width: 90 }}
              />
            </div>
          </>
        )}
        <Button variant="accent" type="submit" disabled={!title || !startsAt}>
          Add event(s)
        </Button>
      </form>
      )}
      {notice && (
        <p
          style={{
            ...monoLabelStyle,
            margin: "10px 0 0",
            color: "var(--accent)",
          }}
        >
          {notice}
        </p>
      )}
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </Section>
  );
}

// ── Pre/Post-roll (ADR 0052) ─────────────────────────────────────────────────

const DEFAULT_ROLL_DWELL = 8000;

// The cave operator's view of the pre/post/hold-roll playlists. Shown only when
// the super-admin has enabled allow_roll_contrib for the cave (or to a super-admin,
// LSC-365). One unified playlist per roll (ADR 0254, LSC-619): the ACTIVE sequence
// (visible globals + the cave's own items, mixed, labelled A/B/C… in playback
// order — drag or ↑/↓ to reorder) and a HIDDEN shelf to re-show from. Every item
// can be hidden without deletion except a global the super-admin locked (pinned).
export function CaveRollTab({ caveId }: { caveId: string }) {
  const [pre, setPre] = useState<RollItem[]>([]);
  const [post, setPost] = useState<RollItem[]>([]);
  const [hold, setHold] = useState<RollItem[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    fetchCaveRollItems(caveId)
      .then((pl) => {
        setPre(pl.pre);
        setPost(pl.post);
        setHold(pl.hold ?? []);
      })
      .catch((err: unknown) => setError(errMsg(err)));
  }, [caveId]);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <CaveRollColumn caveId={caveId} roll="pre" title="Pre-roll (before the show)" items={pre} onChange={load} onError={setError} />
      <CaveRollColumn caveId={caveId} roll="post" title="Post-roll (after the show)" items={post} onChange={load} onError={setError} />
      <CaveRollColumn caveId={caveId} roll="hold" title="Hold (paused-video slate)" items={hold} onChange={load} onError={setError} />
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </div>
  );
}

const ROLL_LABEL: Record<RollName, string> = { pre: "Pre-roll", post: "Post-roll", hold: "Hold" };

const rollRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexWrap: "wrap" as const,
  padding: "8px 10px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 8,
  background: "var(--surface-raised, transparent)",
};

const slotBadgeStyle = {
  minWidth: 28,
  height: 28,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 6,
  fontFamily: "var(--font-display, inherit)",
  fontWeight: 700,
  color: "var(--bg, #000)",
  background: "var(--accent)",
};

function RollItemChips({ it }: { it: RollItem }) {
  return (
    <>
      <Chip color="var(--accent)">{it.kind.toUpperCase()}</Chip>
      <Chip>{it.source === "cave" ? "OURS" : "GLOBAL"}</Chip>
      {it.source === "global" && !it.removable && <Chip>LOCKED</Chip>}
    </>
  );
}

function CaveRollColumn({
  caveId,
  roll,
  title,
  items,
  onChange,
  onError,
}: {
  caveId: string;
  roll: RollName;
  title: string;
  items: RollItem[];
  onChange: () => void;
  onError: (msg: string) => void;
}) {
  const [itemTitle, setItemTitle] = useState("");
  const [kind, setKind] = useState<"image" | "video">("image");
  const [url, setUrl] = useState("");
  const [dwell, setDwell] = useState(String(DEFAULT_ROLL_DWELL));
  const [busy, setBusy] = useState(false);
  // The dragged id lives in a ref (read by drop handlers without a stale closure)
  // and mirrors into state only for the dimmed-row affordance.
  const dragRef = useRef<string | null>(null);
  const [dragId, setDragIdState] = useState<string | null>(null);
  const setDragId = (id: string | null) => {
    dragRef.current = id;
    setDragIdState(id);
  };

  const ownCount = items.filter((it) => it.source === "cave").length;
  // The server lists visible items first, in playback order, then the hidden ones.
  const byId = useMemo(() => new Map(items.map((it) => [it.id, it])), [items]);
  const serverOrder = useMemo(() => items.filter((it) => !it.suppressed).map((it) => it.id), [items]);
  const hidden = items.filter((it) => it.suppressed);
  // Optimistic local order so a drag/move reflects instantly. It's keyed to the
  // server listing it was made against, so fresh server data (a reload, a hide)
  // wins on the same render — never a frame of stale/empty sequence.
  const serverSig = serverOrder.join("|");
  const [pending, setPending] = useState<{ sig: string; order: string[] } | null>(null);
  const order = pending && pending.sig === serverSig ? pending.order : serverOrder;
  const setOrder = (next: string[]) => setPending({ sig: serverSig, order: next });

  const guard = (p: Promise<unknown>) => {
    setBusy(true);
    p.then(onChange)
      .catch((err: unknown) => onError(errMsg(err)))
      .finally(() => setBusy(false));
  };

  const saveOrder = (next: string[]) => {
    if (next === order) return;
    setOrder(next);
    setBusy(true);
    setCaveRollOrder(caveId, roll, next)
      .catch((err: unknown) => {
        setPending(null); // drop the rejected optimistic order (e.g. a stale 409)
        onError(errMsg(err));
      })
      .finally(() => {
        setBusy(false);
        onChange(); // reload: confirms the save, or restores truth after a 409
      });
  };

  const addByUrl = () => {
    if (!url.trim()) return;
    guard(
      createCaveRollItem(caveId, {
        roll,
        kind,
        url: url.trim(),
        title: itemTitle.trim(),
        position: ownCount,
        durationMs: kind === "image" ? Number(dwell) || DEFAULT_ROLL_DWELL : 0,
      }),
    );
    setItemTitle("");
    setUrl("");
  };

  const upload = (file: File, reset: () => void) => {
    const field = file.type.startsWith("video") ? "video" : "image";
    guard(
      uploadCaveRollItem(caveId, field, file, {
        roll,
        title: itemTitle.trim(),
        position: ownCount,
        durationMs: field === "image" ? Number(dwell) || DEFAULT_ROLL_DWELL : 0,
      }),
    );
    setItemTitle("");
    reset();
  };

  const deleteButton = (it: RollItem) => (
    <Button size="sm" variant="muted" aria-label={`Delete ${it.title}`} disabled={busy} onClick={() => guard(deleteCaveRollItem(caveId, it.id))}>
      Delete
    </Button>
  );

  return (
    <Section title={title}>
      <p style={{ ...monoLabelStyle, margin: "12px 0 6px" }}>Active sequence — plays A → Z, then loops</p>
      <ol aria-label={`${ROLL_LABEL[roll]} playback order`} style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
        {order.map((id, i) => {
          const it = byId.get(id);
          if (!it) return null;
          const label = it.title || "(untitled)";
          return (
            <li
              key={id}
              draggable={!busy}
              aria-label={`${rollSlotLabel(i)}: ${label}`}
              onDragStart={(e) => {
                setDragId(id);
                e.dataTransfer?.setData("text/plain", id);
              }}
              onDragOver={(e) => dragRef.current && e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                if (dragRef.current) saveOrder(dropRollId(order, dragRef.current, id));
                setDragId(null);
              }}
              onDragEnd={() => setDragId(null)}
              style={{ ...rollRowStyle, cursor: busy ? "default" : "grab", opacity: dragId === id ? 0.5 : 1 }}
            >
              <span aria-hidden style={{ color: "var(--text-dim)" }}>⠿</span>
              <span style={slotBadgeStyle}>{rollSlotLabel(i)}</span>
              <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)", flex: "1 1 160px" }}>{label}</span>
              <RollItemChips it={it} />
              <div style={{ display: "flex", gap: 6 }}>
                <Button size="sm" variant="muted" aria-label={`Move ${label} up`} disabled={busy || i === 0} onClick={() => saveOrder(moveRollId(order, id, -1))}>
                  ↑
                </Button>
                <Button size="sm" variant="muted" aria-label={`Move ${label} down`} disabled={busy || i === order.length - 1} onClick={() => saveOrder(moveRollId(order, id, 1))}>
                  ↓
                </Button>
                {it.removable ? (
                  <Button size="sm" variant="muted" aria-label={`Hide ${label}`} disabled={busy} onClick={() => guard(suppressGlobalRollItem(caveId, id))}>
                    Hide
                  </Button>
                ) : (
                  <span style={monoLabelStyle}>Pinned</span>
                )}
                {it.source === "cave" && deleteButton(it)}
              </div>
            </li>
          );
        })}
      </ol>
      {order.length === 0 && (
        <p style={{ margin: 0, fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
          Nothing plays — {items.length ? "show an item below or add one." : `no ${roll}-roll items yet.`}
        </p>
      )}

      {hidden.length > 0 && (
        <>
          <p style={{ ...monoLabelStyle, margin: "14px 0 6px" }}>Hidden ({hidden.length}) — not playing; Show appends to the end</p>
          <ul aria-label={`${ROLL_LABEL[roll]} hidden items`} style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
            {hidden.map((it) => (
              <li key={it.id} style={{ ...rollRowStyle, opacity: 0.7 }}>
                <span style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)", flex: "1 1 160px" }}>{it.title || "(untitled)"}</span>
                <RollItemChips it={it} />
                <Chip>HIDDEN</Chip>
                <div style={{ display: "flex", gap: 6 }}>
                  <Button size="sm" variant="accent" aria-label={`Show ${it.title}`} disabled={busy} onClick={() => guard(unsuppressGlobalRollItem(caveId, it.id))}>
                    Show
                  </Button>
                  {it.source === "cave" && deleteButton(it)}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <form
        aria-label={`Add ${roll}-roll item`}
        onSubmit={(e) => {
          e.preventDefault();
          addByUrl();
        }}
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: "1px solid var(--border-subtle)",
          display: "flex",
          alignItems: "flex-end",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <TextField label="Title" name={`${roll}-title`} value={itemTitle} onChange={setItemTitle} />
        <div style={{ display: "flex", gap: 2 }} role="group" aria-label="Kind">
          {(["image", "video"] as const).map((k) => (
            <ViewTab key={k} label={k.toUpperCase()} active={kind === k} onClick={() => setKind(k)} />
          ))}
        </div>
        <TextField label="Asset URL" name={`${roll}-url`} value={url} onChange={setUrl} />
        {kind === "image" && (
          <div style={fieldColumnStyle}>
            <FieldLabel htmlFor={`${roll}-dwell`}>Dwell (ms)</FieldLabel>
            <input
              id={`${roll}-dwell`}
              type="number"
              min={500}
              aria-label="Dwell milliseconds"
              value={dwell}
              onChange={(e) => setDwell(e.target.value)}
              style={{ ...wellInputStyle, width: 100 }}
            />
          </div>
        )}
        <Button variant="accent" type="submit" disabled={busy || !url.trim()}>
          Add by URL
        </Button>
        <label style={{ ...monoLabelStyle, cursor: "pointer", color: "var(--accent)" }}>
          ＋ Upload file
          <input
            type="file"
            accept="image/*,video/*"
            aria-label={`Upload ${roll}-roll file`}
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              const el = e.target;
              if (f) upload(f, () => (el.value = ""));
            }}
          />
        </label>
      </form>
    </Section>
  );
}

// ── Tickets ──────────────────────────────────────────────────────────────────

interface Series {
  seriesId: string;
  label: string;
}

// canWrite gates pricing authority (ADR 0037): without catalog-write the tab is
// read-only — prices are shown, but the create form and Delete controls are
// hidden (a cave-scoped legend/director can see the catalog, not change it).
export function CaveTicketsTab({
  caveId,
  canWrite = false,
}: {
  caveId: string;
  canWrite?: boolean;
}) {
  const [ticketTypes, setTicketTypes] = useState<TicketType[]>([]);
  const [events, setEvents] = useState<CaveEvent[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Rename-in-place + reorder (LSC-572 / ADR 0251) — admin (catalog-write) only.
  const [renameId, setRenameId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [kind, setKind] = useState<TicketKind>("single");
  const [eventId, setEventId] = useState("");
  const [seriesId, setSeriesId] = useState("");
  const [windowWeeks, setWindowWeeks] = useState("8");
  const [customIds, setCustomIds] = useState<string[]>([]);

  const load = useCallback(() => {
    fetchCaveTicketTypes(caveId)
      .then(setTicketTypes)
      .catch((err: unknown) => setError(errMsg(err)));
    fetchCaveEvents(caveId)
      .then(setEvents)
      .catch((err: unknown) => setError(errMsg(err)));
  }, [caveId]);

  useEffect(() => {
    setError(null);
    load();
  }, [load]);

  // Only SELLABLE events may back a new ticket (LSC-341): non-archived and not yet
  // started — matching the backend's forward-looking coverage (ADR 0152), so a new
  // Single/Custom ticket can never be created against a CaveCast that already ran
  // (which would be born unsellable). The management list (above) still shows all.
  const sellableEvents = useMemo(
    () => events.filter((e) => !e.archivedAt && new Date(e.startsAt).getTime() >= Date.now()),
    [events],
  );

  // Distinct series present in the events, labelled by the first event + count.
  const series = useMemo<Series[]>(() => {
    const byId = new Map<string, CaveEvent[]>();
    for (const e of events) {
      if (!e.seriesId) continue;
      const list = byId.get(e.seriesId) ?? [];
      list.push(e);
      byId.set(e.seriesId, list);
    }
    return [...byId.entries()].map(([sid, list]) => ({
      seriesId: sid,
      label: `${list[0].title} (${list.length})`,
    }));
  }, [events]);

  const toggleCustom = (id: string) => {
    setCustomIds((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
  };

  const submit = () => {
    if (!name || !price) return;
    setError(null);
    const priceCents = Math.round(Number(price) * 100);
    const base = { name, kind, priceCents };
    let body;
    if (kind === "single") body = { ...base, eventId };
    else if (kind === "season") body = { ...base, seriesId };
    else if (kind === "all_access")
      body = { ...base, windowWeeks: Number(windowWeeks) };
    else body = { ...base, eventIds: customIds };
    createCaveTicketType(caveId, body)
      .then(() => {
        setName("");
        setPrice("");
        setCustomIds([]);
        load();
      })
      .catch((err: unknown) => setError(errMsg(err)));
  };

  // Rename a ticket type in place (LSC-572 / ADR 0251): id + relationships preserved.
  const saveRename = (ttId: string) => {
    const n = renameValue.trim();
    if (!n) return;
    setError(null);
    renameCaveTicketType(caveId, ttId, n)
      .then(() => {
        setRenameId(null);
        load();
      })
      .catch((err: unknown) => setError(errMsg(err)));
  };
  // ticketTypes already arrives in server (display) order, so ↑/↓ swap within it (LSC-572).
  const moveTicket = (ttId: string, dir: -1 | 1) => {
    const ids = ticketTypes.map((t) => t.id);
    const i = ids.indexOf(ttId);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setError(null);
    reorderCaveTicketTypes(caveId, ids)
      .then(load)
      .catch((err: unknown) => setError(errMsg(err)));
  };

  return (
    <Section title="Tickets">
      <div style={{ marginTop: 12 }}>
        {ticketTypes.map((t, ti) => (
          <div key={t.id}>
          <div style={rowStyle}>
            <span
              style={{ fontSize: "var(--fs-body-2)", color: "var(--text-hi)" }}
            >
              {t.name}
            </span>
            <Chip color="var(--accent)">{formatKind(t.kind)}</Chip>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-2xs)",
                letterSpacing: "var(--ls-label)",
                color: "var(--text-hi)",
              }}
            >
              {formatPrice(t.priceCents)}
            </span>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--fs-2xs)",
                letterSpacing: "var(--ls-label)",
                color: "var(--text-dim)",
              }}
            >
              {t.eventCount} EVENTS
            </span>
            {t.nextEventAt && (
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--fs-2xs)",
                  letterSpacing: "var(--ls-label)",
                  color: "var(--text-dim)",
                }}
              >
                {formatDateTime(t.nextEventAt)}
              </span>
            )}
            {canWrite && (
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <Button
                  size="sm"
                  variant={renameId === t.id ? "accent" : "muted"}
                  aria-label={`Rename ticket ${t.name}`}
                  onClick={() => {
                    setRenameValue(t.name);
                    setRenameId((id) => (id === t.id ? null : t.id));
                  }}
                >
                  Rename
                </Button>
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Move ${t.name} up`}
                  disabled={ti <= 0}
                  onClick={() => moveTicket(t.id, -1)}
                >
                  ↑
                </Button>
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Move ${t.name} down`}
                  disabled={ti >= ticketTypes.length - 1}
                  onClick={() => moveTicket(t.id, 1)}
                >
                  ↓
                </Button>
                <Button
                  size="sm"
                  variant="muted"
                  aria-label={`Delete ticket ${t.name}`}
                  onClick={() => {
                    setError(null);
                    deleteCaveTicketType(caveId, t.id)
                      .then(load)
                      .catch((err: unknown) => setError(errMsg(err)));
                  }}
                >
                  Delete
                </Button>
              </div>
            )}
          </div>
          {canWrite && renameId === t.id && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: "4px 0 8px" }}>
              <TextField
                label="New name"
                name={`rename-ticket-${t.id}`}
                value={renameValue}
                onChange={setRenameValue}
                placeholder="Ticket name"
              />
              <Button size="sm" variant="accent" aria-label={`Save name for ${t.name}`} onClick={() => saveRename(t.id)}>
                Save
              </Button>
              <Button size="sm" variant="muted" aria-label="Cancel rename" onClick={() => setRenameId(null)}>
                Cancel
              </Button>
            </div>
          )}
          </div>
        ))}
        {ticketTypes.length === 0 && !error && (
          <p
            style={{
              margin: 0,
              fontSize: "var(--fs-xs)",
              color: "var(--text-dim)",
            }}
          >
            No ticket types.
          </p>
        )}
      </div>

      {canWrite && (
        <form
          aria-label="Add ticket type"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--border-subtle)",
            display: "grid",
            gap: 12,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Name"
              name="ticket-name"
              value={name}
              onChange={setName}
            />
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="ticket-price">Price ($)</FieldLabel>
              <input
                id="ticket-price"
                type="number"
                min={0}
                step="0.01"
                aria-label="Price dollars"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                style={{ ...wellInputStyle, width: 110 }}
              />
            </div>
          </div>

          <div
            style={{ display: "flex", flexWrap: "wrap", gap: 2 }}
            role="group"
            aria-label="Kind"
          >
            {(["single", "season", "all_access", "custom"] as TicketKind[]).map(
              (k) => (
                <ViewTab
                  key={k}
                  label={formatKind(k)}
                  active={kind === k}
                  onClick={() => setKind(k)}
                />
              ),
            )}
          </div>

          {kind === "single" && (
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="ticket-event">Event</FieldLabel>
              <select
                id="ticket-event"
                aria-label="Event"
                value={eventId}
                onChange={(e) => setEventId(e.target.value)}
                style={wellInputStyle}
              >
                <option value="">Select event…</option>
                {sellableEvents.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.title} — {formatDateTime(e.startsAt)}
                  </option>
                ))}
              </select>
              {sellableEvents.length === 0 && (
                <p style={{ margin: "6px 0 0", fontSize: "var(--fs-xs)", color: "var(--text-dim)" }}>
                  No upcoming CaveCasts to sell — schedule one first.
                </p>
              )}
            </div>
          )}

          {kind === "season" && (
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="ticket-series">Series</FieldLabel>
              <select
                id="ticket-series"
                aria-label="Series"
                value={seriesId}
                onChange={(e) => setSeriesId(e.target.value)}
                style={wellInputStyle}
              >
                <option value="">Select series…</option>
                {series.map((s) => (
                  <option key={s.seriesId} value={s.seriesId}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {kind === "all_access" && (
            <div style={fieldColumnStyle}>
              <FieldLabel htmlFor="ticket-window">Window weeks</FieldLabel>
              <input
                id="ticket-window"
                type="number"
                min={1}
                aria-label="Window weeks"
                value={windowWeeks}
                onChange={(e) => setWindowWeeks(e.target.value)}
                style={{ ...wellInputStyle, width: 90 }}
              />
            </div>
          )}

          {kind === "custom" && (
            <div style={{ display: "grid", gap: 6 }}>
              <span style={monoLabelStyle}>Events</span>
              {sellableEvents.map((e) => (
                <label
                  key={e.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    fontSize: "var(--fs-xs)",
                    color: "var(--text-hi)",
                  }}
                >
                  <DiamondCheckbox
                    checked={customIds.includes(e.id)}
                    aria-label={`Cover ${e.title}`}
                    onChange={() => toggleCustom(e.id)}
                  />
                  {e.title} — {formatDateTime(e.startsAt)}
                </label>
              ))}
              {sellableEvents.length === 0 && (
                <p
                  style={{
                    margin: 0,
                    fontSize: "var(--fs-xs)",
                    color: "var(--text-dim)",
                  }}
                >
                  No upcoming CaveCasts to cover — schedule one first.
                </p>
              )}
            </div>
          )}

          <div>
            <Button variant="accent" type="submit" disabled={!name || !price}>
              Add ticket type
            </Button>
          </div>
        </form>
      )}
      {error && (
        <p role="alert" style={alertStyle}>
          {error}
        </p>
      )}
    </Section>
  );
}
